import React, { useState } from "react";
import { cn } from "../../utils/cn";

interface TextareaFieldProps {
  label?: string;
  id?: string;
  value: string;
  onChange: (value: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  textareaClassName?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  icon?: React.ReactNode;
  rows?: number;
  loading?: boolean;
}

export const FleetrifyTextareaField: React.FC<TextareaFieldProps> = ({
  label,
  id,
  value,
  onChange,
  placeholder,
  className,
  labelClassName,
  textareaClassName,
  error,
  disabled,
  required,
  icon,
  rows = 4,
  loading = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isFocused || value?.trim().length > 0;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-sm font-medium transition-colors duration-200",
            error ? "text-red-500" : isActive ? "text-[#143887]" : "text-black",
            labelClassName
          )}
        >
          {label} {required && <span className="text-[#EB5757]">*</span>}
        </label>
      )}
      <div className="relative">
        <textarea
          id={id}
          rows={rows}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            "w-full px-4 py-3 rounded-md placeholder:text-xs text-xs border-[#1438874D] border transition-colors duration-200 resize-none",
            "focus:outline-none focus:ring-[0.5] focus:ring-[#143887]",
            error ? "border-red-500 focus:ring-red-500" : isActive ? "border-[#143887]" : "border-gray-300",
            (disabled || loading) && "text-gray-600 bg-[#F5F6FA] cursor-not-allowed",
            textareaClassName
          )}
          aria-invalid={!!error}
          disabled={disabled || loading}
        />
        {/* Spinner or Icon */}
        {loading ? (
          <div className="absolute top-3 right-4">
            <div className="h-4 w-4 border-2 border-gray-300 border-t-[#143887] rounded-full animate-spin" />
          </div>
        ) : (
          icon && <div className="absolute top-3 right-4">{icon}</div>
        )}
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};
